import {useMemo} from "react";
import {Line} from "@react-three/drei";
import {stateColors} from "../StateColors";
import {type ShapeProps} from "../../controller/ShapeController";
import {lineWidth, useShapeEffects} from "./ShapeEffects.ts";


export function InteractiveCube({position, diameter, minZ, state, controller, orientation, touchingObject}: ShapeProps) {

    const meshRef = controller.getMeshRef();
    const color = stateColors[state];

    const {edgeColor, emissiveIntensity, metalness, roughness, opacity} =
        useShapeEffects(controller, touchingObject, color, position.z, minZ);

    // cube geometry basics
    const edgeLength = diameter * 0.8;
    const width = edgeLength;
    const height = edgeLength;
    const depth = edgeLength;

    // convert orientation from degrees { rotY?, rotX? } to radians
    const rotationY = ((orientation?.rotY ?? 0) * Math.PI) / 180;
    const rotationX = -((orientation?.rotX ?? 0) * Math.PI) / 180;

    const cubeEdges = useMemo(() => {
        const w = width / 2;
        const h = height / 2;
        const d = depth / 2;

        // 8 vertices of the cube
        const v0: [number, number, number] = [-w, -h, -d];
        const v1: [number, number, number] = [w, -h, -d];
        const v2: [number, number, number] = [w, h, -d];
        const v3: [number, number, number] = [-w, h, -d];
        const v4: [number, number, number] = [-w, -h, d];
        const v5: [number, number, number] = [w, -h, d];
        const v6: [number, number, number] = [w, h, d];
        const v7: [number, number, number] = [-w, h, d];

        // all 12 edges
        return [
            // front
            [v0, v1], [v1, v2], [v2, v3], [v3, v0],
            // back
            [v4, v5], [v5, v6], [v6, v7], [v7, v4],
            // connections
            [v0, v4], [v1, v5], [v2, v6], [v3, v7]
        ] as [[number, number, number], [number, number, number]][];
    }, []);

    return (<>
            <mesh ref={meshRef} position={[position.x, position.y, position.z]}
                  rotation={[rotationX, rotationY, 0]}
                  {...controller.getPointerHandlers()}
            >
                <boxGeometry args={[width, height, depth]}/>
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={emissiveIntensity}
                    transparent opacity={opacity}
                    roughness={roughness}
                    metalness={metalness}
                />
                <pointLight color={color} intensity={1.5} distance={10}/>
            </mesh>
            {cubeEdges.map((edge, i) => (
                <Line
                    key={i}
                    points={edge}
                    position={[position.x, position.y, position.z]}
                    rotation={[rotationX, rotationY, 0]}
                    color={edgeColor ?? color}
                    lineWidth={lineWidth}
                />
            ))}
        </>
    )
}
